// Update Existing / import: category names in the file that match no existing
// category. The user maps each distinct name once (to an existing category or
// one they create); every row naming it then takes that category. Nothing is
// created without the user choosing to.

export const categoryNameKey = (name) => String(name == null ? '' : name).trim().toLowerCase();

// tasks: [{ categoryName, transactionType, changeIndex, fieldIndex, ... }]
// -> [{ key, name, transactionType, transactionTypes, count, tasks }] in first-seen order.
export function groupUnknownCategories(tasks = []) {
  const groups = new Map();
  for (const task of tasks) {
    const key = categoryNameKey(task.categoryName);
    if (!key) continue;
    if (!groups.has(key)) {
      groups.set(key, { key, name: String(task.categoryName).trim(), transactionType: task.transactionType || 'expense', transactionTypes: [], count: 0, tasks: [] });
    }
    const group = groups.get(key);
    group.tasks.push(task);
    group.count += 1;
    const type = task.transactionType || 'expense';
    if (!group.transactionTypes.includes(type)) group.transactionTypes.push(type);
  }
  return [...groups.values()];
}

// Applies one category to every unresolved change whose file name is `key`.
// Returns { items, remaining } without mutating the inputs.
export function applyCategoryToName(reviewItems, tasks, key, categoryId, categories = []) {
  const id = Number(categoryId);
  if (!Number.isFinite(id) || id <= 0) return { items: reviewItems, remaining: tasks };
  const cat = categories.find((c) => Number(c.id) === id);
  const items = reviewItems.map((item) => ({ ...item, changes: [...item.changes] }));
  const remaining = [];
  for (const task of tasks) {
    if (categoryNameKey(task.categoryName) !== key) { remaining.push(task); continue; }
    const item = items[task.changeIndex];
    const ch = item && item.changes[task.fieldIndex];
    if (!ch) continue;
    item.changes[task.fieldIndex] = { ...ch, newValue: id, newDisplay: cat ? cat.name : String(id), needsAction: false };
  }
  return { items, remaining };
}

// Text for a server refusal that lists unknown category names (code UNKNOWN_CATEGORIES).
export function unknownCategoriesMessage(error) {
  const data = error && error.response && error.response.data;
  if (!data || data.code !== 'UNKNOWN_CATEGORIES' || !Array.isArray(data.unknown_categories)) return null;
  const lines = data.unknown_categories.map((u) => {
    const n = Array.isArray(u.rows) ? u.rows.length : 0;
    return `- "${u.name}" (${n} ${n === 1 ? 'row' : 'rows'})`;
  });
  return `These categories don't exist yet:\n${lines.join('\n')}\n\nMap each one to an existing category or create it in the Categories step, then apply again. Nothing was saved.`;
}

// Import review: choosing a category for one row also fills every other row
// that still needs one and carries the same bank category name and type.
export function mapSameBankCategory(rows = [], mappings = {}, index, categoryId) {
  const next = { ...mappings, [index]: categoryId };
  const row = rows[index];
  if (categoryId == null || !row) return next;
  const key = categoryNameKey(row._bankCategory);
  if (!key) return next;
  rows.forEach((other, i) => {
    if (i === index || !other || !other._needsCategoryReview) return;
    if (next[i] != null) return;
    if (categoryNameKey(other._bankCategory) !== key) return;
    if ((other.transaction_type || '') !== (row.transaction_type || '')) return;
    next[i] = categoryId;
  });
  return next;
}
